import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { ListingDialog } from "@/admin/ListingDialog";
import { ConfirmDialog } from "@/admin/ConfirmDialog";

export default function Listings() {
  const { groups, refresh } = useSite();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/schools", { params: { q, group: group === "all" ? "" : group } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, group]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const afterChange = () => { setEditing(null); load(); refresh(); };
  const remove = async () => {
    try {
      await adminApi.delete(`/admin/schools/${deleting.id}`);
      toast.success("Listing deleted");
      setDeleting(null);
      afterChange();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-listings-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div><h1 className="text-2xl font-bold text-charcoal">Listings</h1><p className="text-sm text-muted-foreground">{rows.length} schools</p></div>
        <Button onClick={() => setEditing("new")} className="bg-brand hover:bg-brand-dark" data-testid="listing-create-button"><Plus className="h-4 w-4 mr-1" />New listing</Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search schools…" className="pl-9 bg-white" data-testid="listing-search-input" />
        </div>
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-56 bg-white" data-testid="listing-group-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All groups</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border bg-white overflow-x-auto">
        <Table data-testid="listings-table">
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Group</TableHead><TableHead className="w-36 text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`listing-row-${r.slug}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground">{r.group}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Link to={`/school/${r.slug}`} target="_blank" className="inline-flex p-2 text-muted-foreground hover:text-brand" data-testid={`listing-view-${r.slug}`}><ExternalLink className="h-4 w-4" /></Link>
                  <button type="button" onClick={() => setEditing(r)} className="p-2 text-muted-foreground hover:text-brand" data-testid={`listing-edit-${r.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setDeleting(r)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`listing-delete-${r.slug}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={3} className="text-center py-10 text-muted-foreground" data-testid="listings-empty">No listings found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ListingDialog open={!!editing} listing={editing === "new" ? null : editing} groups={groups} onClose={() => setEditing(null)} onSaved={afterChange} />
      <ConfirmDialog open={!!deleting} title="Delete listing?" description={`"${deleting?.name}" will be permanently removed.`} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
}
